import CoursewareItemResolver from 'services/coursewareItem/CoursewareItemResolver';
import client from '../../ConnectionManager/apolloClient';
import {
  activityById as activityByIdQuery,
  defaultActivity as defaultActivityQuery,
} from './query/activity.gql';
import {
  staticDataFull as staticDataFullQuery,
  staticDataCourseWithoutInteractive as staticDataCourseWithoutInteractiveQuery,
  staticDataLesson as staticDataLessonQuery,
} from './query/staticData.gql';
import {
  staticDataElementId as staticDataElementIdQuery,
  staticDataDefault as staticDataDefaultQuery,
  learnerDataElementId as learnerDataElementIdQuery,
  learnerDataDefaultFirst as learnerDataDefaultFirstQuery,
} from './query/element.gql';
import {
  activityByIdLearner as activityByIdLearnerQuery,
} from './query/activityLearner.gql';
import {
  learnerDataFull as learnerDataFullQuery,
  learnerDataCourseWithoutInteractive as learnerDataCourseWithoutInteractiveQuery,
  learnerDataLesson as learnerDataLessonQuery,
} from './query/learnerData.gql';
import { interactiveById } from './query/interactive.gql';
import { interactiveById as interactiveByIdQuery } from './query/interactiveById.gql';
import { interactiveByIdLearner as interactiveByIdLearnerQuery } from './query/interactiveByIdLearner.gql';

import { evaluationById } from './query/evaluation.gql';
import gtmService from '../../utils/gtm/gtmService';
import { httpErrorCodes } from '../../utils/gtm/constants';
import { ACTIVITY, INTERACTIVE } from '../coursewareItem/coursewareTypes';
import StaticDataQuery from './query/StaticDataQuery';
import LearnerDataQuery from './query/LearnerDataQuery';

const defaultPolicy = { fetchPolicy: 'network-only', errorPolicy: 'all' };
const cachePolicy = { fetchPolicy: 'cache-first', errorPolicy: 'all' };

export const getDefaultActivity = async ({ deploymentId, cohortId }) => {
  const result = await client.query({
    query: defaultActivityQuery,
    variables: { deploymentId, cohortId },
    ...cachePolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentType: ACTIVITY,
      errorCode: httpErrorCodes.NOT_FOUND,
      error: result.errors[0],
    });
  }
  return result;
};

export const getStaticData = async ({
  deploymentId,
  cohortId,
  courseId,
  unitId,
  lessonId,
  screenId,
}) => {
  let staticDataQuery;
  if (!courseId || !unitId) {
    staticDataQuery = staticDataLessonQuery;
  } else if (courseId && !screenId) {
    staticDataQuery = staticDataCourseWithoutInteractiveQuery;
  } else {
    staticDataQuery = staticDataFullQuery;
  }
  const result = await client.query({
    query: staticDataQuery,
    variables: {
      deploymentId, cohortId, courseId, unitId, lessonId, screenId,
    },
    ...cachePolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentType: ACTIVITY,
      errorCode: httpErrorCodes.NOT_FOUND,
      error: result.errors[0],
    });
  }
  return result?.data;
};

export const getStaticDataElementId = async ({ deploymentId, cohortId, elementId }) => {
  const result = await client.query({
    query: staticDataElementIdQuery,
    variables: {
      deploymentId, cohortId, elementId,
    },
    ...cachePolicy,
  });
  return result?.data;
};

export const getStaticDataDefault = async ({ deploymentId, cohortId }) => {
  const result = await client.query({
    query: staticDataDefaultQuery,
    variables: {
      deploymentId, cohortId,
    },
    ...cachePolicy,
  });
  return result?.data;
};

export const getLearnerDataElementId = async ({ deploymentId, cohortId, elementId }) => {
  const result = await client.query({
    query: learnerDataElementIdQuery,
    variables: {
      deploymentId, cohortId, elementId,
    },
    ...cachePolicy,
  });
  return result?.data;
};

export const getLearnerDataDefaultFirst = async ({ deploymentId, cohortId }) => {
  const result = await client.query({
    query: learnerDataDefaultFirstQuery,
    variables: {
      deploymentId, cohortId,
    },
    ...cachePolicy,
  });
  return result?.data;
};

export const getStaticDataFromSocket = async ({
  deploymentId,
  cohortId,
  courseId,
  unitId,
  lessonId,
  screenId,
}) => {
  let staticDataQuery;
  if (!courseId || !unitId) {
    staticDataQuery = StaticDataQuery.staticDataLessonQuery(cohortId, deploymentId, lessonId, screenId);
  } else if (courseId && !screenId) {
    staticDataQuery = StaticDataQuery.staticDataCourseWithoutInteractiveQuery(cohortId, deploymentId, courseId, unitId, lessonId);
  } else {
    staticDataQuery = StaticDataQuery.staticDataFullQuery(cohortId, deploymentId, courseId, unitId, lessonId, screenId);
  }
  const result = await CoursewareItemResolver.sendGraphqlQuery(staticDataQuery);
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentType: ACTIVITY,
      errorCode: httpErrorCodes.NOT_FOUND,
      error: result.errors[0],
    });
  }
  return result?.data;
};

export const getActivityById = async ({ deploymentId, cohortId, activityId }) => {
  const result = await client.query({
    query: activityByIdQuery,
    variables: { deploymentId, cohortId, activityId },
    ...cachePolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentId: activityId,
      contentType: ACTIVITY,
      errorCode: httpErrorCodes.NOT_FOUND,
      error: result.errors[0],
    });
  }
  return result;
};

export const getActivityByIdLearner = async ({ deploymentId, cohortId, activityId }) => {
  const result = await client.query({
    query: activityByIdLearnerQuery,
    variables: { deploymentId, cohortId, activityId },
    ...defaultPolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentId: activityId,
      contentType: ACTIVITY,
      errorCode: httpErrorCodes.NOT_FOUND,
      error: result.errors[0],
    });
  }
  return result;
};

export const getInteractive = async ({ deploymentId, cohortId, interactiveId }) => {
  const result = await client.query({
    query: interactiveById,
    variables: { deploymentId, cohortId, interactiveId },
    ...defaultPolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentId: interactiveId,
      contentType: INTERACTIVE,
      errorCode: httpErrorCodes.UNPROCESSABLE_ENTITY,
      error: result.errors[0],
    });
  }
  return result;
};

export const getInteractiveById = async ({ deploymentId, cohortId, interactiveId }) => {
  const result = await client.query({
    query: interactiveByIdQuery,
    variables: { deploymentId, cohortId, interactiveId },
    ...cachePolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentId: interactiveId,
      contentType: INTERACTIVE,
      errorCode: httpErrorCodes.UNPROCESSABLE_ENTITY,
      error: result.errors[0],
    });
  }
  return result;
};

export const getInteractiveByIdLearner = async ({ deploymentId, cohortId, interactiveId }) => {
  const result = await client.query({
    query: interactiveByIdLearnerQuery,
    variables: { deploymentId, cohortId, interactiveId },
    ...defaultPolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentId: interactiveId,
      contentType: INTERACTIVE,
      errorCode: httpErrorCodes.UNPROCESSABLE_ENTITY,
      error: result.errors[0],
    });
  }
  return result;
};

const getWalkable = () => {
};

export const getCompletedWalkableByEvaluationId = async ({ deploymentId, cohortId, evaluationId }) => {
  // Since this is a static query (should return the same result each time) we can
  // use a more aggressive fetch policy.
  const result = await client.query({
    query: evaluationById,
    variables: { deploymentId, cohortId, evaluationId },
    ...cachePolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      errorCode: httpErrorCodes.UNPROCESSABLE_ENTITY,
      error: result.errors[0],
    });
  }
  return result?.data.learn.cohort.deployment[0].evaluation;
};

export const getLearnerData = async ({
  deploymentId,
  cohortId,
  courseId,
  unitId,
  lessonId,
  screenId,
}) => {
  let learnerDataQuery;
  if (!courseId || !unitId) {
    learnerDataQuery = learnerDataLessonQuery;
  } else if (courseId && !screenId) {
    learnerDataQuery = learnerDataCourseWithoutInteractiveQuery;
  } else {
    learnerDataQuery = learnerDataFullQuery;
  }
  const result = await client.query({
    query: learnerDataQuery,
    variables: {
      deploymentId, cohortId, courseId, unitId, lessonId, screenId,
    },
    ...defaultPolicy,
  });
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentType: ACTIVITY,
      errorCode: httpErrorCodes.NOT_FOUND,
      error: result.errors[0],
    });
  }
  return result?.data;
};

export const getLearnerDataFromSocket = async ({
  deploymentId,
  cohortId,
  courseId,
  unitId,
  lessonId,
  screenId,
}) => {
  let learnerDataQuery;
  if (!courseId || !unitId) {
    learnerDataQuery = LearnerDataQuery.learnerDataLesson(cohortId, deploymentId, lessonId, screenId);
  } else if (courseId && !screenId) {
    learnerDataQuery = LearnerDataQuery.learnerDataCourseWithoutInteractive(cohortId, deploymentId, courseId, unitId, lessonId);
  } else {
    learnerDataQuery = LearnerDataQuery.learnerDataFull(cohortId, deploymentId, courseId, unitId, lessonId, screenId);
  }
  const result = await CoursewareItemResolver.sendGraphqlQuery(learnerDataQuery);
  if (result?.errors?.length) {
    gtmService.pushPageViewErrorEvent({
      contentType: ACTIVITY,
      errorCode: httpErrorCodes.NOT_FOUND,
      error: result.errors[0],
    });
  }
  return result?.data;
};

export default {
  getDefaultActivity,
  getActivityById,
  getActivityByIdLearner,
  getInteractive,
  getInteractiveById,
  getInteractiveByIdLearner,
  getWalkable,
  getCompletedWalkableByEvaluationId,
  getLearnerData,
};
